import styled, { css } from 'styled-components';

export const Root = styled.nav`
  display: inline-flex;
  align-items: center;
  min-width: 0;
  /* Tabular numerals on the breadcrumb so paths with versions / IDs line
     up consistently. Cheap nicety, no downside on plain text. */
  font-variant-numeric: tabular-nums;
`;

export const List = styled.ol`
  display: inline-flex;
  align-items: center;
  margin: 0;
  padding: 0;
  list-style: none;
  min-width: 0;
  gap: 4px;
`;

export const Item = styled.li`
  display: inline-flex;
  align-items: center;
  min-width: 0;
`;

export const Separator = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  margin: 0 2px;
  user-select: none;
  ${({ theme: { colors = {} } }) => css`
    color: ${colors.onSurfaceVariant ?? '#C7C4D7'};
  `}
  opacity: 0.55;
  font-size: 14px;
  & > svg,
  & > .material-symbols-rounded {
    font-size: 16px;
    width: 16px;
    height: 16px;
  }
`;

interface SegmentProps {
  $current: boolean;
  $interactive: boolean;
}

/**
 * Each segment renders as a button (when interactive) or a span. We need
 * `color: inherit` to defeat the UA-default `color: buttontext` on real
 * `<button>` so the segment matches the surrounding text colour the
 * breadcrumb is sitting in.
 */
export const Segment = styled.button.attrs<SegmentProps>(() => ({
  // Always set `type="button"`; otherwise the browser defaults to
  // `type="submit"` which can accidentally submit a wrapping <form>.
  // The non-interactive case is handled by `tabIndex={-1}` on the JSX
  // side, not by leaving `type` unset.
  type: 'button' as const,
}))<SegmentProps>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  margin: 0;
  padding: 2px 6px;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  font-size: 13px;
  line-height: 1.2;
  border-radius: ${({ theme: { borderRadius = {} } }) =>
    borderRadius.extrasmall ?? '4px'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  appearance: none;
  -webkit-appearance: none;
  text-align: left;
  transition:
    background ${({ theme: { motion } }) => motion?.duration?.fast ?? '120ms'}
      ${({ theme: { motion } }) => motion?.easing?.standard ?? 'cubic-bezier(0.2,0,0,1)'},
    color ${({ theme: { motion } }) => motion?.duration?.fast ?? '120ms'}
      ${({ theme: { motion } }) => motion?.easing?.standard ?? 'cubic-bezier(0.2,0,0,1)'};

  ${({ $current, $interactive, theme: { colors = {} } }) => {
    if ($current) {
      return css`
        color: ${colors.onSurface ?? '#E4E1ED'};
        font-weight: 600;
        cursor: ${$interactive ? 'pointer' : 'default'};
        ${$interactive &&
        css`
          &:hover {
            background: ${colors.surfaceContainerHigh ?? '#292932'};
          }
        `}
      `;
    }
    return css`
      color: ${colors.onSurfaceVariant ?? '#C7C4D7'};
      cursor: ${$interactive ? 'pointer' : 'default'};
      ${$interactive &&
      css`
        &:hover {
          background: ${colors.surfaceContainerHigh ?? '#292932'};
          color: ${colors.onSurface ?? '#E4E1ED'};
        }
      `}
    `;
  }}

  &:focus-visible {
    outline: 2px solid
      ${({ theme: { colors = {} } }) => colors.primary ?? '#C1C1FF'};
    outline-offset: 2px;
  }

  & > .seg-trailing {
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
    opacity: 0.7;
  }
`;

export const Label = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
